import { supabase } from './supabase';

export interface SyncOperation {
  id: string;
  type: 'insert' | 'update' | 'delete';
  table: string;
  data: any;
  timestamp: number;
  retries: number;
}

export interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  pendingOperations: number;
  failedOperations: number;
}

class SyncManager {
  private operationQueue: SyncOperation[] = [];
  private isSyncing = false;
  private listeners: Set<(status: SyncStatus) => void> = new Set();
  private syncInterval: NodeJS.Timeout | null = null;
  private lastSyncTime: number | null = null;
  private isOnline = navigator.onLine;
  private currentUserId: string | null = null;
  // Stable handler references so removeEventListener in destroy() actually works.
  private onlineHandler = () => this.handleOnline();
  private offlineHandler = () => this.handleOffline();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onlineHandler);
      window.addEventListener('offline', this.offlineHandler);

      const saved = localStorage.getItem('sync-queue');
      if (saved) {
        try {
          this.operationQueue = JSON.parse(saved);
        } catch (e) {
          console.error('Failed to load sync queue:', e);
        }
      }

      this.startSyncInterval();
    }
  }

  async initialize() {
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Sync initialization timeout')), 5000)
    );

    try {
      // Use getSession() (reads the locally-stored session) rather than
      // getUser() (a network call to the auth server that can kick off a token
      // refresh). Many stores call into the auth layer concurrently on login;
      // routing them through getSession avoids a burst of refresh attempts
      // racing on the single-use refresh token (which makes Supabase emit
      // SIGNED_OUT — i.e. the app signing itself out).
      const authCheck = supabase.auth.getSession();
      const result = await Promise.race([authCheck, timeout]) as any;
      const { data: { session } = { session: null }, error } = result || {};
      const user = session?.user ?? null;

      if (user && !error) {
        this.currentUserId = user.id;
        this.processSyncQueue().catch(err => {
          console.warn('Initial sync queue processing failed:', err);
        });
      }
    } catch (error) {
      console.warn('Sync manager initialization failed, running in offline mode:', error);
      this.isOnline = false;
      this.notifyListeners();
    }
  }

  setUserId(userId: string | null) {
    this.currentUserId = userId;
    if (userId) {
      this.processSyncQueue();
    }
  }

  private startSyncInterval() {
    this.syncInterval = setInterval(() => {
      if (this.isOnline && this.operationQueue.length > 0) {
        this.processSyncQueue();
      }
    }, 30000);
  }

  private handleOnline() {
    this.isOnline = true;
    this.notifyListeners();
    this.processSyncQueue();
  }

  private handleOffline() {
    this.isOnline = false;
    this.notifyListeners();
  }

  subscribe(listener: (status: SyncStatus) => void): () => void {
    this.listeners.add(listener);
    listener(this.getStatus());
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    const status = this.getStatus();
    this.listeners.forEach(listener => listener(status));
  }

  getStatus(): SyncStatus {
    return {
      isOnline: this.isOnline,
      isSyncing: this.isSyncing,
      lastSyncTime: this.lastSyncTime,
      pendingOperations: this.operationQueue.length,
      failedOperations: this.operationQueue.filter(op => op.retries >= 3).length
    };
  }

  async queueOperation(operation: Omit<SyncOperation, 'id' | 'timestamp' | 'retries'>) {
    const op: SyncOperation = {
      ...operation,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      retries: 0
    };

    this.operationQueue.push(op);
    this.saveQueue();
    this.notifyListeners();

    if (this.isOnline && this.currentUserId) {
      await this.processSyncQueue();
    }
  }

  private saveQueue() {
    try {
      localStorage.setItem('sync-queue', JSON.stringify(this.operationQueue));
    } catch (e) {
      console.error('Failed to save sync queue:', e);
    }
  }

  async processSyncQueue() {
    if (this.isSyncing || !this.isOnline || !this.currentUserId) return;
    if (this.operationQueue.length === 0) return;

    this.isSyncing = true;
    this.notifyListeners();

    const operations = [...this.operationQueue];
    const processedIds: string[] = [];
    const failedOps: SyncOperation[] = [];

    const processTimeout = setTimeout(() => {
      console.warn('Sync queue processing timeout, will retry later');
    }, 30000);

    try {
      for (const operation of operations) {
        try {
          await this.executeOperation(operation);
          processedIds.push(operation.id);
        } catch (error) {
          console.error('Sync operation failed:', error);
          operation.retries += 1;
          if (operation.retries < 3) {
            failedOps.push(operation);
          }
        }
      }

      this.operationQueue = this.operationQueue.filter(
        op => !processedIds.includes(op.id)
      );
      this.operationQueue.push(...failedOps);

      if (processedIds.length > 0) {
        this.lastSyncTime = Date.now();
      }
    } catch (error) {
      console.error('Critical sync error:', error);
    } finally {
      clearTimeout(processTimeout);
      this.saveQueue();
      this.isSyncing = false;
      this.notifyListeners();
    }
  }

  private async executeOperation(operation: SyncOperation): Promise<void> {
    const { type, table, data } = operation;

    switch (type) {
      case 'insert': {
        const { error: insertError } = await supabase
          .from(table)
          .insert(data);
        if (insertError) throw insertError;
        break;
      }

      case 'update': {
        const { id, ...updateData } = data;
        const { error: updateError } = await supabase
          .from(table)
          .update(updateData)
          .eq('id', id);
        if (updateError) throw updateError;
        break;
      }

      case 'delete': {
        const { error: deleteError } = await supabase
          .from(table)
          .delete()
          .eq('id', data.id);
        if (deleteError) throw deleteError;
        break;
      }
    }
  }

  async safeAuthCheck(): Promise<{ user: any; error: any }> {
    try {
      // getSession() reads the cached session locally — no auth-server round
      // trip and no per-call token refresh. This is called on every store
      // initialize() and every cloud read/write, so using getUser() here meant
      // ~20 concurrent auth-server hits on login, racing to refresh the
      // rotating refresh token → spurious SIGNED_OUT. RLS still validates the
      // token server-side on the actual data request, so this is safe.
      const result = await supabase.auth.getSession();
      return { user: result.data.session?.user ?? null, error: result.error };
    } catch (error) {
      return { user: null, error };
    }
  }

  async safeFetch<T>(
    query: () => Promise<any>,
    fallback: T
  ): Promise<{ data: T; error: any }> {
    try {
      if (!this.isOnline) {
        return { data: fallback, error: new Error('Offline') };
      }

      const result = await query();
      return { data: result.data || fallback, error: result.error };
    } catch (error) {
      console.error('Fetch failed:', error);
      return { data: fallback, error };
    }
  }

  async forceSync() {
    await this.processSyncQueue();
  }

  clearFailedOperations() {
    this.operationQueue = this.operationQueue.filter(op => op.retries < 3);
    this.saveQueue();
    this.notifyListeners();
  }

  destroy() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandler);
      window.removeEventListener('offline', this.offlineHandler);
    }
  }
}

export const syncManager = new SyncManager();
